import {
	useCallback,
	useContext
} from 'react';
import createDataContext from '../services/createDataContext';
import { mapLookupTypes } from '../services/utils';

import {
	getAgencyOrganizations,
	getManagementOrganizations,
	getNotificationDigestTypes,
	getNotificationTypes,
	getUserRoles
} from '../api/nora/lookups';

import adminReducer, {
	SET_AGENCIES,
	SET_MANAGEMENT,
	SET_NOTIFICATION_DIGEST_TYPES,
	SET_NOTIFICATION_TYPES,
	SET_TABLE_STATE,
	SET_USER,
	SET_USER_LIST,
	SET_USER_ROLES
} from '../reducers/adminReducer';

export let controller = new AbortController();

const initialState = {
	agencyOrganizations: [],
	managementOrganizations: [],
	notificationDigestTypes: [],
	notificationTypes: [],
	tableState: {
		filters: [],
		pageIndex: 0,
		pageSize: 10,
		selectedIds: [],
		sortBy: []
	},
	user: null,
	userList: [],
	userRoles: []
};

// init functions
const setAgencyOrganizations = async dispatch => {
	try {
		const [ { types } ] = await getAgencyOrganizations( controller.signal );

		const typesToMap = types.map( ( { organizationID, name } ) => ( {
			id: organizationID,
			name
		} ) );

		const mappedTypes = mapLookupTypes( typesToMap );

		SET_AGENCIES( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const setManagementOrganizations = async dispatch => {
	try {
		const [ { types } ] = await getManagementOrganizations( controller.signal );

		const typesToMap = types.map( ( { organizationID, name } ) => ( {
			id: organizationID,
			name
		} ) );

		const mappedTypes = mapLookupTypes( typesToMap );

		SET_MANAGEMENT( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const setNotificationDigestTypes = async dispatch => {
	try {
		const [ { types } ] = await getNotificationDigestTypes( controller.signal );

		const mappedTypes = mapLookupTypes( types );

		SET_NOTIFICATION_DIGEST_TYPES( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const setUserRoles = async dispatch => {
	try {
		const [ { types } ] = await getUserRoles( controller.signal );

		const mappedTypes = mapLookupTypes( types );

		SET_USER_ROLES( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const onInit = async dispatch => {
	controller = new AbortController();

	await setAgencyOrganizations( dispatch );
	await setManagementOrganizations( dispatch );
	await setNotificationDigestTypes( dispatch );
	await setUserRoles( dispatch );
};

const onCleanup = () => {
	controller.abort();
};

export const {
	StateContext,
	ActionContext,
	Provider
} = createDataContext( {
	reducer: adminReducer,
	initialState,
	controller,
	onInit,
	onCleanup
} );

export function useAdminState() {
	const state = useContext( StateContext );

	return state;
}

export function useAdminAction() {
	const dispatch = useContext( ActionContext );

	const clearUser = useCallback( () => {
		SET_USER( dispatch, controller.signal )( null );
	}, [ dispatch ] );

	const setNotificationTypes = useCallback( async roleId => {
		try {
			const types = await getNotificationTypes( roleId, controller.signal );

			SET_NOTIFICATION_TYPES( dispatch, controller.signal )( types );
		} catch ( error ) {
		// handle
		}
	}, [ dispatch ] );

	const setTableState = useCallback( updateFunc => {
		SET_TABLE_STATE( dispatch, controller.signal )( updateFunc );
	}, [ dispatch ] );

	const setUser = useCallback( data => {
		SET_USER( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setUserList = useCallback( data => {
		SET_USER_LIST( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	return {
		clearUser,
		setNotificationTypes,
		setTableState,
		setUser,
		setUserList
	};
}
